import React, { FunctionComponent, useState } from 'react'
import { Button, ButtonProps } from '@afterpaytouch/core'
import { LoaderModal } from '../LoaderModal'
import { useCloseCheckout } from '../../utils/close'
import { create } from 'react-modal-promise'
import { LeaveCheckoutModal } from '../LeaveCheckoutModal'
import { useCheckoutStatus, useCountry, useIsConsumerLendingOrder, useIsExtendedFraudAlert, useIsRoute, useExperiment } from '../../state'
import { Route } from '../../config/router'
import { CheckoutStatus } from '../../model'
import { TrackingEvent } from '../../model/monitoring'
import { Country } from '@afterpay/types'
import { useCallAbandonCheckout } from './hooks'

interface Props extends ButtonProps {
  onClick?: () => void
}

export const CloseCheckout: FunctionComponent<Props> = ({ children, onClick = () => {}, type = 'button', size = 'Large', ...buttonProps }) => {
  const [showLoaderModal, setShowLoaderModal] = useState(false)
  const close = useCloseCheckout()
  const isConsumerLending = useIsConsumerLendingOrder()
  const checkoutStatus = useCheckoutStatus()
  const showLeaveModal = create(LeaveCheckoutModal)
  const isTerminalErrorRoute = useIsRoute(Route.TERMINAL_ERROR)
  const isPaymentOptionsRoute = useIsRoute(Route.PAYMENT_OPTIONS)
  const isPayMonthlyOnlyRoute = useIsRoute(Route.PAY_MONTHLY_ONLY)
  const isSummaryRoute = useIsRoute(Route.SUMMARY)
  const country = useCountry()
  const isExtendedFraudAlert = useIsExtendedFraudAlert()
  const callAbandonCheckout = useCallAbandonCheckout()
  const summaryLeaveExperiment = useExperiment(
    'SummaryLeaveCheckout',
    isSummaryRoute && !isConsumerLending && country === Country.US && checkoutStatus !== CheckoutStatus.Closed && !isExtendedFraudAlert && !isTerminalErrorRoute
  )

  // Show LeaveCheckoutModal on these pages: payment-options, add-payment-method for consumer lending, consumer-lending-summary, truth-in-lending
  // and pay-monthly-only page in US pay monthly only flow.
  // Also shown on summary page for paylater (non-consumer lending) orders when experiment is in treatment.
  const shouldShowLeaveModal =
    country === Country.US &&
    !isTerminalErrorRoute &&
    checkoutStatus !== CheckoutStatus.Closed &&
    (isConsumerLending ||
      isPaymentOptionsRoute ||
      isPayMonthlyOnlyRoute ||
      (isSummaryRoute && !isConsumerLending && (summaryLeaveExperiment.value === 'A' || summaryLeaveExperiment.value === 'B'))) &&
    !isExtendedFraudAlert

  const handleClick = async (): Promise<void> => {
    if (shouldShowLeaveModal) {
      try {
        await showLeaveModal(
          isSummaryRoute && !isConsumerLending
            ? {
                messageContext: 'paylater',
                invertedButtons: summaryLeaveExperiment.value === 'B',
                trackingEvents: {
                  viewed: TrackingEvent.VIEWED_SUMMARY_LEAVE_CHECKOUT_MODAL,
                  leave: TrackingEvent.PRESSED_SUMMARY_LEAVE_CHECKOUT_MODAL_LEAVE,
                  cancel: TrackingEvent.PRESSED_SUMMARY_LEAVE_CHECKOUT_MODAL_CANCEL,
                  stay: TrackingEvent.PRESSED_SUMMARY_LEAVE_CHECKOUT_MODAL_STAY,
                },
              }
            : undefined
        )
      } catch (e) {
        return
      }
    }

    setShowLoaderModal(true)

    onClick()
    await Promise.race([callAbandonCheckout(), new Promise((resolve) => setTimeout(resolve, 1000))])
    close()
  }

  return (
    <>
      <Button type={type} size={size} {...buttonProps} onClick={handleClick}>
        {children}
      </Button>
      {showLoaderModal && <LoaderModal />}
    </>
  )
}
